import axios, { AxiosResponse, AxiosError } from "axios";
import { getCookie } from "@util/cookie";
import { fatalErrorLog } from "@util/sentry";
import {
  CVUploadResponse,
  GetMyCVResponse,
  CVWorkPreferences,
  ProfilePictureResponse,
  TempCVUploadResponse,
  TempSupportingFileUploadResponse,
  SupportingFilesListResponse,
  DeleteSupportingFileResponse,
  SupportingFile,
} from "@type/cv";
import { catchErrorLog } from "@util/sentry";

const apiURL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;

/**
 * Uploads a CV file to the server
 * @param file - The CV file to upload (PDF format)
 * @returns Promise with upload response data
 */
export const uploadCV = async (file: File): Promise<CVUploadResponse> => {
  try {
    const token = getCookie("authToken") || getCookie("tempAuthToken");

    if (!token) {
      return {
        success: false,
        status: 401,
        message: "No authentication token found",
      };
    }

    // Create FormData object to send file
    const formData = new FormData();
    formData.append("file", file);

    const response: AxiosResponse = await axios({
      url: `${apiURL}/cv/upload`,
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: formData,
      timeout: 120000, // 2 minute timeout for file upload
    });

    return {
      success: true,
      status: response.status,
      data: response.data,
    };
  } catch (error) {
    const err = error as AxiosError;
    const responseData = err.response?.data as Record<string, unknown>;

    // The caller has usually already shown the file as attached, so a silent
    // failure here means the candidate submits an application with no CV and
    // nobody finds out. This resolves rather than rejects, so reporting has to
    // happen here — the caller's `.catch()` never runs.
    fatalErrorLog(
      "[cv] upload failed — the candidate's CV never reached the server",
      {
        http_status: err.response?.status ?? "none (network or timeout)",
        detail: (responseData?.message as string) || err.message,
      },
      { area: "cv_upload" },
    );

    return {
      success: false,
      status: err.response?.status || 500,
      message:
        (responseData?.message as string) ||
        (err.code === "ECONNABORTED"
          ? "Upload request timed out"
          : "Failed to upload CV"),
      error: err,
    };
  }
};

/**
 * Uploads a temporary CV file to the server
 * @param file - The CV file to upload (PDF format)
 * @returns Promise with upload response data
 */
export const uploadTempCV = async (
  file: File
): Promise<TempCVUploadResponse> => {
  try {
    // Create FormData object to send file
    const formData = new FormData();
    formData.append("file", file);

    const response: AxiosResponse = await axios({
      url: `${apiURL}/cv/upload-temp-cv`,
      method: "POST",
      headers: {
        "Content-Type": "multipart/form-data",
      },
      data: formData,
      timeout: 120000, // 2 minute timeout for file upload
    });

    return {
      success: true,
      status: response.status,
      data: response.data.data,
    };
  } catch (error) {
    const err = error as AxiosError;
    const responseData = err.response?.data as Record<string, unknown>;

    if (error instanceof Error) {
      catchErrorLog(`failed to upload candidate CV, err: ${error.message}`);
    }

    return {
      data: { filename: "error" },
      success: false,
      status: err.response?.status || 500,
      message:
        (responseData?.message as string) ||
        (err.code === "ECONNABORTED"
          ? "Upload request timed out"
          : "Failed to upload CV"),
      error: err,
    };
  }
};

/**
 * Uploads a temporary supporting PDF (e.g. transcript, portfolio) during
 * the signup flow. Mirrors uploadTempCV — unauthenticated, returns a
 * server-issued filename uuid that the FE persists until activation.
 * @param file - The PDF file to upload
 */
export const uploadTempSupportingFile = async (
  file: File
): Promise<TempSupportingFileUploadResponse> => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const response: AxiosResponse = await axios({
      url: `${apiURL}/cv/upload-temp-supporting-file`,
      method: "POST",
      headers: {
        "Content-Type": "multipart/form-data",
      },
      data: formData,
      timeout: 120000,
    });

    return {
      success: true,
      status: response.status,
      data: (response.data?.data ?? null) as SupportingFile | null,
    };
  } catch (error) {
    const err = error as AxiosError;
    const responseData = err.response?.data as Record<string, unknown>;

    if (error instanceof Error) {
      catchErrorLog(`failed to upload supporting file, err: ${error.message}`);
    }

    return {
      success: false,
      status: err.response?.status || 500,
      data: null,
      message:
        (responseData?.message as string) ||
        (err.code === "ECONNABORTED"
          ? "Upload request timed out"
          : "Failed to upload supporting file"),
      error: err,
    };
  }
};

/**
 * Deletes a previously-uploaded temporary supporting file. Idempotent —
 * a 404 is treated as success because the FE doesn't care if the file
 * was already pruned by TTL.
 * @param filename - The server-issued filename uuid returned from upload
 */
export const deleteTempSupportingFile = async (
  filename: string
): Promise<DeleteSupportingFileResponse> => {
  try {
    const response: AxiosResponse = await axios({
      url: `${apiURL}/cv/temp-supporting-file/${encodeURIComponent(filename)}`,
      method: "DELETE",
      timeout: 30000,
    });

    return {
      success: true,
      status: response.status,
    };
  } catch (error) {
    const err = error as AxiosError;
    const responseData = err.response?.data as Record<string, unknown>;

    // 404 = already gone, treat as success.
    if (err.response?.status === 404) {
      return { success: true, status: 404 };
    }

    if (error instanceof Error) {
      catchErrorLog(
        `failed to delete temp supporting file, err: ${error.message}`
      );
    }

    return {
      success: false,
      status: err.response?.status || 500,
      message:
        (responseData?.message as string) ||
        "Failed to delete supporting file",
      error: err,
    };
  }
};

/**
 * Lists supporting files for the authenticated user.
 */
export const getMySupportingFiles =
  async (): Promise<SupportingFilesListResponse> => {
    try {
      const token = getCookie("authToken");
      if (!token) {
        return {
          success: false,
          status: 401,
          data: [],
          message: "No authentication token found",
        };
      }

      const response: AxiosResponse = await axios({
        url: `${apiURL}/cv/me/supporting-files`,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        timeout: 30000,
      });

      return {
        success: true,
        status: response.status,
        data: (response.data?.data ?? []) as SupportingFile[],
      };
    } catch (error) {
      const err = error as AxiosError;
      const responseData = err.response?.data as Record<string, unknown>;

      if (error instanceof Error) {
        catchErrorLog(
          `failed to list supporting files, err: ${error.message}`
        );
      }

      return {
        success: false,
        status: err.response?.status || 500,
        data: [],
        message:
          (responseData?.message as string) ||
          "Failed to list supporting files",
        error: err,
      };
    }
  };

/**
 * Uploads a supporting file for the authenticated user (post-registration).
 */
export const uploadSupportingFile = async (
  file: File
): Promise<TempSupportingFileUploadResponse> => {
  try {
    const token = getCookie("authToken");
    if (!token) {
      return {
        success: false,
        status: 401,
        data: null,
        message: "No authentication token found",
      };
    }

    const formData = new FormData();
    formData.append("file", file);

    const response: AxiosResponse = await axios({
      url: `${apiURL}/cv/me/supporting-files`,
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
      data: formData,
      timeout: 120000,
    });

    return {
      success: true,
      status: response.status,
      data: (response.data?.data ?? null) as SupportingFile | null,
    };
  } catch (error) {
    const err = error as AxiosError;
    const responseData = err.response?.data as Record<string, unknown>;

    if (error instanceof Error) {
      catchErrorLog(`failed to upload supporting file, err: ${error.message}`);
    }

    return {
      success: false,
      status: err.response?.status || 500,
      data: null,
      message:
        (responseData?.message as string) ||
        (err.code === "ECONNABORTED"
          ? "Upload request timed out"
          : "Failed to upload supporting file"),
      error: err,
    };
  }
};

/**
 * Deletes a supporting file for the authenticated user.
 */
export const deleteSupportingFile = async (
  id: string
): Promise<DeleteSupportingFileResponse> => {
  try {
    const token = getCookie("authToken");
    if (!token) {
      return {
        success: false,
        status: 401,
        message: "No authentication token found",
      };
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/cv/me/supporting-files/${encodeURIComponent(id)}`,
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      timeout: 30000,
    });

    return {
      success: true,
      status: response.status,
    };
  } catch (error) {
    const err = error as AxiosError;
    const responseData = err.response?.data as Record<string, unknown>;

    if (err.response?.status === 404) {
      return { success: true, status: 404 };
    }

    if (error instanceof Error) {
      catchErrorLog(
        `failed to delete supporting file, err: ${error.message}`
      );
    }

    return {
      success: false,
      status: err.response?.status || 500,
      message:
        (responseData?.message as string) ||
        "Failed to delete supporting file",
      error: err,
    };
  }
};

/**
 * Gets the user's CV from the server
 * @returns Promise with CV data
 */
export const getMyCV = async (): Promise<GetMyCVResponse> => {
  try {
    const token = getCookie("authToken") || getCookie("tempAuthToken");
    const response: AxiosResponse = await axios({
      url: `${apiURL}/cv/my-cv`,
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      timeout: 120000, // 2 min timeout
    });

    return {
      code: 200,
      message: "success",
      data: response.data.data,
      errors: [],
    };
  } catch (error) {
    const err = error as AxiosError;
    const responseData = err.response?.data as Record<string, unknown>;

    if (error instanceof Error) {
      catchErrorLog(
        `failed to get current candidate CV, err: ${error.message}`
      );
    }

    return {
      code: err.response?.status || 500,
      message: (responseData?.message as string) || "Failed to get CV",
      data: {
        CV_data: {
          name: "",
          position: "",
          professional_summary: "",
          email: "",
          country_code: "",
          phone: "",
          location: "",
          skills: [],
          work_experience: [],
          education: [],
          certifications: [],
          awards: [],
          publications: [],
          projects: [],
          avatar: "",
        },
        pdf_path: "",
        uploaded_at: "",
        filename: "",
        profile_picture_path: "",
        summary_data: {
          years_of_experience: 0,
          relevant_tags: [],
          highest_education: "Unknown",
        },
      },
      errors: [
        err.code === "ECONNABORTED"
          ? "Request timed out"
          : (responseData?.message as string) || "Failed to get CV",
      ],
    };
  }
};

/**
 * Gets a CV by ID
 * @param cv_id - The ID of the CV to retrieve
 * * @returns Promise with CV data
 */
export const getCVById = async (cv_id: string): Promise<any> => {
  try {
    const token = getCookie("authToken");
    const response: AxiosResponse = await axios({
      url: `${apiURL}/cv/${cv_id}`,
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      timeout: 120000, // 2 min timeout
    });

    return response.data.data;
  } catch (error) {
    const err = error as AxiosError;
    console.error("Error fetching CV by ID:", err);

    return undefined;
  }
};

/**
 * Updates the user's CV data
 * @param cvData - The CV data to update
 * @returns Promise with update response data
 */
export const updateCV = async (
  cvData: GetMyCVResponse["data"],
  job_id?: string
): Promise<GetMyCVResponse> => {
  try {
    const token = getCookie("authToken") || getCookie("tempAuthToken");

    if (!token) {
      return {
        code: 401,
        message: "No authentication token found",
        data: {
          CV_data: {
            name: "",
            position: "",
            professional_summary: "",
            email: "",
            country_code: "",
            phone: "",
            location: "",
            skills: [],
            work_experience: [],
            education: [],
            certifications: [],
            awards: [],
            publications: [],
            projects: [],
            avatar: "",
          },
          pdf_path: "",
          uploaded_at: "",
          profile_picture_path: "",
          summary_data: {
            years_of_experience: 0,
            relevant_tags: [],
            highest_education: "Unknown",
          },
          filename: "",
        },
        errors: ["Authentication required"],
      };
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/cv/update-cv`,
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      data: {
        job_id: job_id || undefined,
        CV_data: cvData?.CV_data,
      },
    });

    return response.data;
  } catch (error) {
    const err = error as AxiosError;
    const responseData = err.response?.data as Record<string, unknown>;

    if (error instanceof Error) {
      catchErrorLog(`failed to update CV candidate, err: ${error.message}`);
    }

    return {
      code: err.response?.status || 500,
      message: (responseData?.message as string) || "Failed to update CV",
      data: {
        CV_data: {
          name: "",
          position: "",
          professional_summary: "",
          email: "",
          country_code: "",
          phone: "",
          location: "",
          skills: [],
          work_experience: [],
          education: [],
          certifications: [],
          awards: [],
          publications: [],
          projects: [],
          //Replace with real avatar
          avatar: "",
        },
        summary_data: {
          years_of_experience: 0,
          relevant_tags: [],
          highest_education: "Unknown",
        },
        filename: "",
        pdf_path: "",
        uploaded_at: "",
        profile_picture_path: "",
      },
      errors: [
        err.code === "ECONNABORTED"
          ? "Request timed out"
          : (responseData?.message as string) || "Failed to update CV",
      ],
    };
  }
};

/**
 * Gets the user's work preferences from the server
 * @returns Promise with work preferences data
 */
export const getWorkPreferences = async (): Promise<{
  success: boolean;
  status: number;
  data?: CVWorkPreferences;
  message?: string;
  error?: AxiosError;
}> => {
  try {
    const token = getCookie("authToken");

    if (!token) {
      return {
        success: false,
        status: 401,
        message: "No authentication token found",
      };
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/cv/work-preferences`,
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      timeout: 10000, // 10 second timeout
    });

    return {
      success: true,
      status: response.status,
      data: response.data.data,
    };
  } catch (error) {
    const err = error as AxiosError;
    const responseData = err.response?.data as Record<string, unknown>;

    return {
      success: false,
      status: err.response?.status || 500,
      message:
        (responseData?.message as string) || "Failed to get work preferences",
      error: err,
    };
  }
};

/**
 * Create the user's work preferences on the server
 * @param preferences - The work preferences data to update
 * @returns Promise with work preferences data
 */
export const createWorkPreferences = async (
  preferences: CVWorkPreferences
): Promise<{
  success: boolean;
  status: number;
  data?: CVWorkPreferences;
  message?: string;
  error?: AxiosError;
}> => {
  try {
    const token = getCookie("authToken");

    if (!token) {
      return {
        success: false,
        status: 401,
        message: "No authentication token found",
      };
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/cv/work-preferences`,
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      data: preferences,
      timeout: 10000, // 10 second timeout
    });

    return {
      success: true,
      status: response.status,
      data: response.data,
    };
  } catch (error) {
    const err = error as AxiosError;
    const responseData = err.response?.data as Record<string, unknown>;

    return {
      success: false,
      status: err.response?.status || 500,
      message:
        (responseData?.message as string) ||
        "Failed to update work preferences",
      error: err,
    };
  }
};

/**
 * Updates the user's work preferences on the server
 * @param preferences - The work preferences data to update
 * @returns Promise with work preferences data
 */
export const updateWorkPreferences = async (
  preferences: CVWorkPreferences
): Promise<{
  success: boolean;
  status: number;
  data?: CVWorkPreferences;
  message?: string;
  error?: AxiosError;
}> => {
  try {
    const token = getCookie("authToken");

    if (!token) {
      return {
        success: false,
        status: 401,
        message: "No authentication token found",
      };
    }

    const response: AxiosResponse = await axios({
      url: `${apiURL}/cv/work-preferences`,
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      data: preferences,
      timeout: 10000, // 10 second timeout
    });

    return {
      success: true,
      status: response.status,
      data: response.data,
      message: response.data.message,
    };
  } catch (error) {
    const err = error as AxiosError;
    const responseData = err.response?.data as Record<string, unknown>;

    return {
      success: false,
      status: err.response?.status || 500,
      message:
        (responseData?.message as string) ||
        "Failed to update work preferences",
      error: err,
    };
  }
};

/**
 * Uploads a profile picture to the server
 * @param file - The image file to upload (JPEG, PNG formats)
 * @returns Promise with upload response data
 */
export const uploadProfilePicture = async (
  file: File
): Promise<{
  success: boolean;
  status: number;
  data?: ProfilePictureResponse;
  message?: string;
  error?: AxiosError;
}> => {
  try {
    const token = getCookie("authToken");

    if (!token) {
      return {
        success: false,
        status: 401,
        message: "No authentication token found",
      };
    }

    // Create FormData object to send file
    const formData = new FormData();
    formData.append("file", file);

    const response: AxiosResponse = await axios({
      url: `${apiURL}/cv/upload-profile-picture`,
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: formData,
      timeout: 30000, // 30 second timeout for image upload
    });

    return {
      success: true,
      status: response.status,
      data: response.data.data,
    };
  } catch (error) {
    const err = error as AxiosError;
    const responseData = err.response?.data as Record<string, unknown>;

    return {
      success: false,
      status: err.response?.status || 500,
      message:
        (responseData?.message as string) ||
        (err.code === "ECONNABORTED"
          ? "Upload request timed out"
          : "Failed to upload profile picture"),
      error: err,
    };
  }
};

export const processUploadedCV = async (
  cv_uuid: string,
  user_id: string,
  name?: string,
  country_code?: string,
  phone_number?: string,
  email?: string
): Promise<any> => {
  const token = getCookie("authToken") || getCookie("tempAuthToken");
  return await axios({
    url: `${apiURL}/cv/process-uploaded-cv`,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: {
      cv_uuid,
      user_id,
      name,
      country_code,
      phone_number,
      email,
    },
    timeout: 300000,
  });
};

export const getCVAnalyze = async (): Promise<any> => {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/cv/analyze`,
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

export const getCVInfoUser = async (cv_id: string, user_id: string): Promise<any> => {
  return await axios({
    url: `${apiURL}/cv/info/${cv_id}/${user_id}`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
};


export const getCurrentCVURL = async (): Promise<any> => {
  return await axios({
    url: `${apiURL}/cv/my-cv-url`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getCookie("tempAuthToken")}`,
    },
  });
}

export const getApplicantCV = async (applicant_id: string): Promise<any> => {
  return await axios({
    url: `${apiURL}/cv/applicant/${applicant_id}`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  })
};

export const applicantJobUploadCV = async (
  job_id: string,
  file: File,
  signal?: AbortSignal
): Promise<any> => {
  const token = getCookie("authToken");

  // Create FormData object to send file
  const formData = new FormData();
  formData.append("file", file);

  return await axios({
    url: `${apiURL}/cv/job/${job_id}/upload`,
    method: "POST",
    headers: {
      "Content-Type": "multipart/form-data",
      "Authorization": `Bearer ${token}`,
    },
    data: formData,
    signal,
    // timeout 5 minutes
    timeout: 300000,
  });
};

export const getApplicantDisplayCV = async (applicant_id: string): Promise<any> => {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/cv/${applicant_id}/display-cv`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`,
    },
  })
};

export const getUserSubmissionJob = async (job_id: string, applicant_id: string): Promise<any> => {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/assessments/${job_id}/applicant/${applicant_id}/submission`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`,
    },
  })
};

export const notifyCVUpload = async (
  cv_ids: string[],
  job_id: string
): Promise<any> => {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/cv/upload/notify`,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: { cv_ids, job_id },
    timeout: 60000,
  });
};

export const getListCVs = async (job_id: string): Promise<any> => {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/cv/job/${job_id}/list-cvs`,
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`,
    },
  })
};

/**
 * Cancel a bulk CV upload batch
 * @param batchId - The batch ID to cancel
 * @returns Promise with cancel response data
 */
export const cancelBulkUploadCV = async (batchId: string): Promise<any> => {
  const token = getCookie("authToken");
  return await axios({
    url: `${apiURL}/cv/bulk-upload-cv/cancel/${batchId}`,
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

/**
 * Queue bulk CV files for processing by SQS consumer
 * @param jobId - The job ID to associate with the CVs
 * @param files - Array of File objects to upload (max 50, PDF/DOC/DOCX)
 * @returns Promise with queue response data
 */
export const bulkUploadCVQueue = async (
  jobId: string,
  files: File[]
): Promise<any> => {
  const token = getCookie("authToken");

  const formData = new FormData();
  formData.append("job_id", jobId);
  files.forEach((file) => {
    formData.append("files", file);
  });

  return await axios({
    url: `${apiURL}/cv/bulk-upload-cv/queue`,
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: formData,
    timeout: 120000,
  });
};